import { NSelect } from '@innersource-nn/novoui-react';
import { Pagination } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PaginationComponentProps } from 'types/Pagination';
import './Pagination.css';

const PaginationComponent: React.FC<PaginationComponentProps> = ({
    items,
    filter,
    setFilter,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const containerRef = useRef<HTMLDivElement>(null);
    const [isSmallContainer, setIsSmallContainer] = useState(false);
    const [rowsPerPageOptions, setRowsPerPageOptions] = useState([
        { key: 10, label: '10', value: 10 },
        { key: 25, label: '25', value: 25 },
        { key: 100, label: '100', value: 100 },
    ]);

    const onPageChange = async (
        e: React.ChangeEvent<unknown>,
        activePage: number,
    ) => {
        if (activePage <= items.pages && activePage !== items.page) {
            const searchParams = new URLSearchParams(location.search);
            searchParams.set('page', activePage.toString());
            navigate({
                pathname: location.pathname,
                search: searchParams.toString(),
            });
            await setFilter({ ...filter, page: activePage });
        }
    };

    const onRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newRowsPerPage = parseInt(e.target.value as string, 10);
        setFilter({ ...filter, pageSize: newRowsPerPage, page: 1 }); // Reset to first page when changing rows per page
    };

    const handleResize = () => {
        if (containerRef.current) {
            const containerWidth = containerRef.current.offsetWidth;
            setIsSmallContainer(containerWidth < 768); // 768px (small)
        }
    };

    useEffect(() => {
        if (
            !!filter?.pageSize &&
            [10, 25, 100].indexOf(filter.pageSize) === -1
        ) {
            // Create a new array with the original options and the new option
            const updatedRowsPerPageOptions = [
                ...rowsPerPageOptions,
                {
                    key: filter.pageSize,
                    label: filter.pageSize.toString(),
                    value: filter.pageSize,
                },
            ];

            // Sort the options based on value
            updatedRowsPerPageOptions.sort((a, b) => a.value - b.value);

            // Update the rowsPerPageOptions array
            setRowsPerPageOptions(updatedRowsPerPageOptions);
        }
    }, [filter?.pageSize]);

    useEffect(() => {
        // Resize component to fit container
        const resizeObserver = new ResizeObserver(handleResize);
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        // Add event listener for window resize
        window.addEventListener('resize', handleResize);

        handleResize(); // Initial check

        return () => {
            if (containerRef.current) {
                resizeObserver.unobserve(containerRef.current);
            }
            // Remove event listener on cleanup
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    return (
        <div
            ref={containerRef}
            className={`pagination-container relative flex flex-wrap items-center mt-8 ${isSmallContainer ? 'flex-col' : 'flex-row'}`}
        >
            {!!items.count && (
                <>
                    <div className="rows-per-page-container flex items-center mb-8 w-full">
                        <span className="mr-8 text-sm">Rows per page </span>
                        <NSelect
                            className="rows-per-page-selector"
                            modelValue={filter.pageSize}
                            onChange={onRowsPerPageChange}
                            options={rowsPerPageOptions}
                            variant="outline-light"
                            size="small"
                            id="rows-per-page"
                        />
                    </div>
                    {items.count > filter.pageSize && (
                        <div
                            className={`pagination-wrapper flex justify-center ${isSmallContainer ? '' : 'pagination-wrapper-absolute'}`}
                        >
                            <Pagination
                                count={items.pages}
                                page={filter.page}
                                onChange={onPageChange}
                                color="primary"
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default PaginationComponent;
